import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import EngineeringIcon from '@mui/icons-material/Engineering';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HandshakeIcon from '@mui/icons-material/Handshake';
import FunctionsIcon from '@mui/icons-material/Functions';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';

const About: React.FC = () => {
  const values = [
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 36 }} />,
      title: 'Technical Excellence',
      description: 'We tackle complex engineering challenges that others shy away from—advanced algorithms, data modeling, and system architecture.',
    },
    {
      icon: <EngineeringIcon sx={{ fontSize: 36 }} />,
      title: 'Deep Expertise',
      description: 'Our background in mathematics, engineering, and computer science enables us to solve problems at their core.',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
      title: 'Business Impact',
      description: 'Every solution we build is designed to deliver measurable improvements in efficiency, accuracy, and scalability.',
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 36 }} />,
      title: 'True Partnership',
      description: 'We work as an extension of your team, understanding your business deeply to deliver solutions that truly fit.',
    },
  ];

  const capabilities = [
    {
      icon: <FunctionsIcon sx={{ fontSize: 32 }} />,
      title: 'Mathematical Modeling',
      description: 'Complex calculations, optimization algorithms, statistical analysis, and predictive modeling built into your applications.',
    },
    {
      icon: <DataObjectIcon sx={{ fontSize: 32 }} />,
      title: 'Data Architecture',
      description: 'Design and migration of database systems, data pipelines, and analytics infrastructure for enterprise-scale operations.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 32 }} />,
      title: 'Process Automation',
      description: 'Replace manual workflows with intelligent automation that reduces errors and frees your team for higher-value work.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      title: 'Enterprise Integration',
      description: 'Connect disparate systems, APIs, and data sources into unified, efficient workflows.',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: `
            radial-gradient(ellipse at 30% 30%, rgba(31, 111, 178, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 70%, rgba(47, 62, 78, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
          pt: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ animation: 'fadeInUp 0.8s ease-out' }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#1f6fb2',
                    fontWeight: 600,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                  }}
                >
                  ABOUT US
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                    color: '#f8fafc',
                  }}
                >
                  Engineers Who
                  <Box component="span" sx={{ color: '#1f6fb2' }}> Transform</Box> Businesses
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  AIM isn't your typical web development shop. We're engineers and problem-solvers 
                  who specialize in digital transformation—taking businesses from outdated, manual 
                  processes to modern, efficient systems that scale.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                  }}
                >
                  Whether it's retiring a decades-old database, automating complex calculations, 
                  or building enterprise-grade software from scratch, we bring the technical depth 
                  needed to solve your hardest problems.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -30,
                      background: 'radial-gradient(circle, rgba(31, 111, 178, 0.15) 0%, transparent 70%)',
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/AIM_logo_transparent.png"
                    alt="AIM Logo"
                    sx={{
                      width: { xs: 200, md: 280 },
                      height: 'auto',
                      position: 'relative',
                      zIndex: 1,
                      filter: 'drop-shadow(0 20px 40px rgba(31, 111, 178, 0.25))',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(180deg, #111827 0%, #0a0f1a 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(31, 111, 178, 0.08) 0%, rgba(47, 62, 78, 0.08) 100%)',
              border: '1px solid rgba(31, 111, 178, 0.15)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #1f6fb2 0%, #4a8fc7 50%, #1f6fb2 100%)',
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              OUR MISSION
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#f8fafc',
                fontWeight: 600,
                lineHeight: 1.5,
                fontStyle: 'italic',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              }}
            >
              "To eliminate inefficiency through technology—retiring outdated systems, 
              automating complex processes, and building solutions that let businesses 
              focus on what they do best."
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Capabilities Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: `
            radial-gradient(ellipse at 50% 0%, rgba(31, 111, 178, 0.08) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              TECHNICAL CAPABILITIES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 600,
                color: '#f8fafc',
                mb: 2,
              }}
            >
              Beyond Basic Development
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              We handle the complex technical challenges that require deep engineering expertise—not 
              just building interfaces, but solving real computational and architectural problems.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {capabilities.map((capability, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.9) 0%, rgba(17, 24, 39, 0.7) 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.08)',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(31, 111, 178, 0.3)',
                      '& .capability-icon': {
                        color: '#1f6fb2',
                      },
                    },
                  }}
                >
                  <Box
                    className="capability-icon"
                    sx={{
                      color: '#64748b',
                      mb: 2,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {capability.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    {capability.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                      lineHeight: 1.7,
                    }}
                  >
                    {capability.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(180deg, #111827 0%, #0a0f1a 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              OUR VALUES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 600,
                color: '#f8fafc',
                mb: 2,
              }}
            >
              What Sets Us Apart
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              We're not just coders—we're engineers who understand business problems 
              and deliver solutions that create lasting impact.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(31, 111, 178, 0.15)',
                      '& .value-icon': {
                        color: '#1f6fb2',
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      className="value-icon"
                      sx={{
                        color: '#64748b',
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {value.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 600,
                        mb: 1.5,
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#94a3b8',
                        lineHeight: 1.7,
                      }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Story Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: `
            radial-gradient(ellipse at 50% 100%, rgba(31, 111, 178, 0.08) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              OUR APPROACH
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 600,
                color: '#f8fafc',
                mb: 4,
              }}
            >
              Why "Always Inventing More"?
            </Typography>
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#94a3b8',
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                We've seen too many businesses held hostage by technology that no longer serves them—
                spreadsheets that have grown into monsters, databases that crash under load, manual 
                processes that eat up hours of skilled workers' time.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#94a3b8',
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                "Always Inventing More" isn't just a name—it's our commitment to continuous improvement. 
                We don't just patch problems; we engineer solutions. We don't just build websites; 
                we transform how businesses operate. And we don't stop at "good enough"—we push for 
                excellence in every line of code.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#94a3b8',
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                }}
              >
                Whether you need to migrate from a legacy system, automate complex calculations, 
                or build an entirely new platform from the ground up, we have the technical expertise 
                to make it happen—and the business acumen to ensure it delivers real value.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
